<script setup lang="ts">
/**
 * Friendly empty state for a list / grid / inbox.
 *
 * Wraps the previous "AppCard with a sad muted paragraph" pattern in a
 * single component so every empty surface in the app stays consistent
 * (icon + title + description + optional CTA) instead of each view
 * rolling its own one-liner. Icons come from the parent via the `icon`
 * prop (any lucide-vue component) so the empty state can hint at the
 * kind of thing missing — categories vs items vs media etc.
 *
 * Usage:
 *   <AppEmptyState
 *     :icon="UtensilsCrossed"
 *     title="No items yet"
 *     description="Create the first one to start building your menu."
 *   >
 *     <AppButton @click="openNew">New item</AppButton>
 *   </AppEmptyState>
 *
 * **When NOT to use:**
 * - The list is loading — that's `AppListSkeleton`. Showing "No items
 *   yet" before the query resolves misleads the user into thinking
 *   the list is empty when it just hasn't loaded.
 * - The list failed to load — that's `AppErrorCard`. An error is not
 *   an empty state.
 * - "Nothing to show, but the user shouldn't act" inline contexts
 *   (a dialog tab body, a card subsection) — a single muted paragraph
 *   is lighter than the whole card-with-icon treatment.
 */
import type { Component } from 'vue'

import AppCard from './AppCard.vue'

interface Props {
  icon?: Component
  title: string
  description?: string
}
defineProps<Props>()
</script>

<template>
  <AppCard padding="lg" class="text-center">
    <div class="mx-auto flex max-w-sm flex-col items-center gap-3">
      <span
        v-if="icon"
        class="inline-flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary"
        aria-hidden="true"
      >
        <component :is="icon" class="size-6" />
      </span>
      <div>
        <h2 class="text-base font-semibold">{{ title }}</h2>
        <p v-if="description" class="mt-1 text-sm text-muted-foreground">{{ description }}</p>
      </div>
      <div v-if="$slots.default" class="mt-1 flex flex-wrap items-center justify-center gap-2">
        <slot />
      </div>
    </div>
  </AppCard>
</template>
